import { useMutation, useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

interface IParams {
  page?: number;
}

export const useGetCapitalList = (params?: IParams) =>
  useQuery<void, APIErrorResponse, any>({
    queryKey: ["get-capital-list"],
    queryFn: async () => {
      return apiClient.get(`/api/capitals`, { params: params ?? {} });
    },
  });

interface IContributorBody {
  id?: number;
  name: string;
  date?: string;
  note?: string | null;
}

export const useAddCapitalContributorMutation = () =>
  useMutation<void, APIErrorResponse, IContributorBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/capitals`, data);
    },
  });

export const useUpdateCapitalContributorMutation = () =>
  useMutation<void, APIErrorResponse, IContributorBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/capitals/${id}`, rest);
    },
  });

export const useDeleteCapitalMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/capitals/${id}`);
    },
  });

export const useGetLegalCapitalList = (params?: IParams) =>
  useQuery<void, APIErrorResponse, any>({
    queryKey: ["get-legal-capital-list"],
    queryFn: async () => {
      return apiClient.get(`/api/legalCapitals`, { params: params ?? {} });
    },
  });

export const useGetLegalCapitalById = ({ id }: { id: string }) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-legal-capital-by-id", id],
    queryFn: async () => {
      return apiClient.get(`/api/legalCapitals/${id}`);
    },
    gcTime: 0,
    enabled: !!id,
  });

export const useAddLegalCapitalMutation = () =>
  useMutation<void, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/legalCapitals`, data);
    },
  });

export const useUpdateLegalCapitalMutation = () =>
  useMutation<void, APIErrorResponse, any>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/legalCapitals/${id}`, rest);
    },
  });

export const useDeleteLegalCapitalMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/legalCapitals/${id}`);
    },
  });

// legal capital payment
export const useAddLegalCapitalPaymentMutation = () =>
  useMutation<void, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/legalCapitalOwnerPayments`, data);
    },
  });

export const useDeleteLegalCapitalPaymentMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/legalCapitalOwnerPayments/${id}`);
    },
  });

// purchase owner payment
export const useAddPurchaseOwnerPaymentMutation = () =>
  useMutation<void, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/purchaseCapitals`, data);
    },
  });

export const useDeletePurchaseOwnerPaymentMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/purchaseCapitals/${id}`);
    },
  });

// oc owner payment
export const useAddOCOwnerPaymentMutation = () =>
  useMutation<void, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/invoiceCapitals`, data);
    },
  });

export const useDeleteOCOwnerPaymentMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/invoiceCapitals/${id}`);
    },
  });

// owners transactions
interface IParams {
  page?: number;
  start_date?: string;
  end_date?: string;
  reference_number?: string;
  supplierId?: string;
  account_id?: string;
  journey_type?: string;
  branches?: number[];
  payment_methods?: number[];
}

export const useGetOwnersTransactionsList = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-owners-transactions-list", params],
    queryFn: async () => {
      return apiClient.get(`/api/capitalsTransactions`, { params });
    },
  });

export const useDownloadOwnersTransactionsList = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["download-owners-trans-list"],
    queryFn: async () => {
      return apiClient.get(`/api/capitalsTransactions/download`, { params });
    },
    enabled: false,
    gcTime: 0,
  });
